import { SvgIconComponent } from '@mui/icons-material';
import {
  Warning,
  LocalFireDepartment,
  LightMode,
  Opacity,
  Thermostat,
  Air,
  CheckCircle,
  Info,
  AcUnit,
  Visibility,
} from '@mui/icons-material';
import { MatchWeatherData } from '@/services/scheduleService';

export interface WeatherAlert {
  icon: SvgIconComponent;
  iconColor: string;
  headline: string;
  detail: string;
  tip: string | null;
  bgColor: string;
}

/**
 * Determine a context-aware weather alert for a match.
 * Returns null when nothing notable — strip should be hidden.
 *
 * Icons here represent the *concern/action* (not weather appearance —
 * that's handled by WeatherIcon via weatherIcons.ts).
 *
 * Priority order (first match wins):
 *  1. Thunderstorm
 *  2. Blizzard
 *  3. Ice/freezing/sleet
 *  4. Extreme heat (>=95F)
 *  5. Very high UV (>=8)
 *  6. Snow
 *  7. Rain (precip >=50% or forecast text)
 *  8. Freezing (<=32F)
 *  9. Cold (33-40F)
 * 10. Fog/haze/mist
 * 11. Windy (>=20 mph, no precip keywords)
 * 12. High UV (6-7) or Moderate UV (4-5)
 * 13. Poor AQI (>=101)
 * 14. Cool (41-54F)
 * 15. Warm (86-94F)
 * 16. Cloudy, nice temps (55-85F)
 * 17. Great conditions (55-85F, clear)
 * 18. Fallback
 */
export function getWeatherAlert(weather: MatchWeatherData): WeatherAlert | null {
  if (!weather.forecastAvailable) return null;

  const temp = weather.temperature;
  const forecast = (weather.shortForecast ?? '').toLowerCase();
  const precip = weather.precipChance;
  const uv = weather.uvIndex;
  const aqi = weather.aqi;

  // No meaningful data — don't show strip when we only have UV (Open-Meteo goes further than NWS)
  const hasNWSData = temp != null || !!weather.shortForecast || precip != null || !!weather.windSpeed;
  if (!hasNWSData && aqi == null) {
    return null;
  }

  const isThunderstorm = forecast.includes('thunder') || forecast.includes('tstorm') || forecast.includes('t-storm');
  const isBlizzard = forecast.includes('blizzard');
  const isIcy = forecast.includes('ice') || forecast.includes('freezing') || forecast.includes('sleet');
  const isSnowy = forecast.includes('snow');
  const isRainy = (precip != null && precip >= 50) ||
    forecast.includes('rain') || forecast.includes('shower') || forecast.includes('drizzle');
  const isFoggy = forecast.includes('fog') || forecast.includes('haze') || forecast.includes('mist');
  const isCloudy = forecast.includes('cloudy') || forecast.includes('overcast');
  const hasPrecipKeyword = isThunderstorm || isRainy || isSnowy || isIcy || isBlizzard;

  // Parse wind speed number from strings like "10 mph" or "15 to 25 mph"
  const windNumbers = (weather.windSpeed ?? '').match(/\d+/g);
  const maxWind = windNumbers ? Math.max(...windNumbers.map(Number)) : 0;
  const isWindy = maxWind >= 20 && !hasPrecipKeyword;

  // Headline determines which keywords are "covered" for detail dedup
  let headlineKeywords: string[] = [];

  // 1. Thunderstorm
  if (isThunderstorm) {
    headlineKeywords = ['thunder', 'tstorm', 't-storm', 'storm'];
    const pct = precip ?? 0;
    const headline = pct >= 70 ? 'Thunderstorms expected' : pct >= 50 ? 'Thunderstorms likely' : 'Storms possible';
    const tip = pct >= 50 ? 'Stay alert' : null;
    return {
      icon: Warning,
      iconColor: '#4A148C',
      headline,
      detail: buildDetail(weather, headlineKeywords),
      tip,
      bgColor: 'rgba(179, 136, 255, 0.12)', // light purple
    };
  }

  // 2. Blizzard
  if (isBlizzard) {
    headlineKeywords = ['blizzard', 'snow'];
    return {
      icon: AcUnit,
      iconColor: '#4A148C',
      headline: 'Blizzard conditions',
      detail: buildDetail(weather, headlineKeywords),
      tip: 'Bundle up',
      bgColor: 'rgba(179, 136, 255, 0.12)', // light purple
    };
  }

  // 3. Ice/freezing/sleet
  if (isIcy) {
    headlineKeywords = ['ice', 'freezing', 'sleet'];
    return {
      icon: AcUnit,
      iconColor: '#0277BD',
      headline: 'Icy conditions',
      detail: buildDetail(weather, headlineKeywords),
      tip: 'Drive carefully',
      bgColor: 'rgba(33, 150, 243, 0.08)', // light blue
    };
  }

  // 4. Extreme heat (>=95F)
  if (temp != null && temp >= 95) {
    return {
      icon: LocalFireDepartment,
      iconColor: '#D32F2F',
      headline: 'Extreme heat',
      detail: buildDetail(weather, headlineKeywords),
      tip: 'Pack extra water',
      bgColor: 'rgba(244, 67, 54, 0.08)', // light pink
    };
  }

  // 5. Very high UV (>=8)
  if (uv != null && uv >= 8) {
    return {
      icon: LightMode,
      iconColor: '#E65100',
      headline: uv >= 11 ? 'Extreme UV' : 'Very high UV',
      detail: buildDetail(weather, headlineKeywords),
      tip: 'Pack sunscreen',
      bgColor: 'rgba(255, 152, 0, 0.08)', // light orange
    };
  }

  // 6. Snow
  if (isSnowy) {
    headlineKeywords = ['snow'];
    return {
      icon: AcUnit,
      iconColor: '#4FC3F7',
      headline: 'Snow expected',
      detail: buildDetail(weather, headlineKeywords),
      tip: 'Dress warmly',
      bgColor: 'rgba(33, 150, 243, 0.08)', // light blue
    };
  }

  // 7. Rain — headline varies by precip chance
  if (isRainy) {
    headlineKeywords = ['rain', 'shower', 'drizzle'];
    const pct = precip ?? 0;
    let headline: string;
    let tip: string | null;
    if (pct >= 70) {
      headline = 'Expect rain';
      tip = 'Bring an umbrella';
    } else if (pct >= 50) {
      headline = 'Rain likely';
      tip = 'Bring an umbrella';
    } else {
      headline = 'Rain possible';
      tip = null;
    }
    return {
      icon: Opacity,
      iconColor: '#0D47A1',
      headline,
      detail: buildDetail(weather, headlineKeywords),
      tip,
      bgColor: 'rgba(255, 193, 7, 0.10)', // light yellow
    };
  }

  // 8. Freezing (<=32F)
  if (temp != null && temp <= 32) {
    return {
      icon: AcUnit,
      iconColor: '#0277BD',
      headline: 'Freezing',
      detail: buildDetail(weather, headlineKeywords),
      tip: 'Bundle up',
      bgColor: 'rgba(33, 150, 243, 0.08)', // light blue
    };
  }

  // 9. Cold (33-40F)
  if (temp != null && temp >= 33 && temp <= 40) {
    return {
      icon: Thermostat,
      iconColor: '#0277BD',
      headline: 'Cold conditions',
      detail: buildDetail(weather, headlineKeywords),
      tip: 'Bundle up',
      bgColor: 'rgba(33, 150, 243, 0.08)', // light blue
    };
  }

  // 10. Fog/haze/mist
  if (isFoggy) {
    headlineKeywords = ['fog', 'haze', 'mist'];
    return {
      icon: Visibility,
      iconColor: '#78909C',
      headline: 'Foggy',
      detail: buildDetail(weather, headlineKeywords),
      tip: 'Drive carefully',
      bgColor: 'rgba(158, 158, 158, 0.08)', // neutral grey
    };
  }

  // 11. Windy (>=20 mph, no precip keywords)
  if (isWindy) {
    return {
      icon: Air,
      iconColor: '#546E7A',
      headline: 'Windy',
      detail: buildDetail(weather, headlineKeywords),
      tip: null,
      bgColor: 'rgba(158, 158, 158, 0.08)', // neutral grey
    };
  }

  // 12. High UV (6-7) or Moderate UV (4-5)
  if (uv != null && uv >= 4) {
    const isHighUv = Math.round(uv) >= 6;
    return {
      icon: LightMode,
      iconColor: isHighUv ? '#E65100' : '#F9A825',
      headline: isHighUv ? 'High UV' : 'Moderate UV',
      detail: buildDetail(weather, headlineKeywords),
      tip: isHighUv ? 'Pack sunscreen' : null,
      bgColor: isHighUv ? 'rgba(255, 152, 0, 0.08)' : 'rgba(255, 235, 59, 0.10)',
    };
  }

  // 13. Poor AQI (>=101)
  if (aqi != null && aqi >= 101) {
    return {
      icon: Air,
      iconColor: '#E65100',
      headline: 'Air quality concern',
      detail: buildDetail(weather, headlineKeywords),
      tip: null,
      bgColor: 'rgba(255, 152, 0, 0.08)', // light orange
    };
  }

  // 14. Cool (41-54F)
  if (temp != null && temp >= 41 && temp <= 54) {
    return {
      icon: Thermostat,
      iconColor: '#4FC3F7',
      headline: 'Cool conditions',
      detail: buildDetail(weather, headlineKeywords),
      tip: 'Dress warmly',
      bgColor: 'rgba(33, 150, 243, 0.08)', // light blue
    };
  }

  // 15. Warm (86-94F)
  if (temp != null && temp >= 86 && temp <= 94) {
    return {
      icon: Thermostat,
      iconColor: '#E65100',
      headline: 'Warm \u2014 stay hydrated',
      detail: buildDetail(weather, headlineKeywords),
      tip: 'Bring water',
      bgColor: 'rgba(255, 152, 0, 0.08)', // light orange
    };
  }

  // 16-17. Nice temps (55-85F, no rain, low UV, good AQI)
  if (
    temp != null && temp >= 55 && temp <= 85 &&
    !isRainy &&
    (uv == null || uv < 4) &&
    (aqi == null || aqi < 101)
  ) {
    const detail = buildDetail(weather, headlineKeywords);
    if (isCloudy) {
      return {
        icon: CheckCircle,
        iconColor: '#558B2F',
        headline: 'Cloudy, nice temps',
        detail,
        tip: null,
        bgColor: 'rgba(76, 175, 80, 0.08)', // light green
      };
    }
    return {
      icon: CheckCircle,
      iconColor: '#2E7D32',
      headline: 'Great conditions',
      detail,
      tip: null,
      bgColor: 'rgba(76, 175, 80, 0.08)', // light green
    };
  }

  // 18. Fallback — show forecast data if we have any
  const detail = buildDetail(weather, headlineKeywords);
  if (detail) {
    return {
      icon: Info,
      iconColor: '#78909C',
      headline: 'Forecast',
      detail,
      tip: null,
      bgColor: 'rgba(158, 158, 158, 0.08)', // neutral grey
    };
  }

  return null;
}

/**
 * Detail line WITHOUT temperature (temp is shown on the headline row).
 * When headlineKeywords are provided, skip the shortForecast text if it
 * only restates what the headline already conveys (e.g. "Chance Rain Showers"
 * when headline is "Rain likely").
 */
function buildDetail(weather: MatchWeatherData, headlineKeywords: string[] = []): string {
  const parts: string[] = [];

  if (weather.shortForecast) {
    const forecastLower = weather.shortForecast.toLowerCase();
    // Include shortForecast only when it adds info beyond what the headline covers
    const coveredByHeadline = headlineKeywords.length > 0 &&
      headlineKeywords.some(kw => forecastLower.includes(kw));
    if (!coveredByHeadline) {
      parts.push(weather.shortForecast);
    }
  }
  if (weather.windSpeed) {
    parts.push(`Wind ${weather.windSpeed}`);
  }
  if (weather.precipChance != null && weather.precipChance >= 10) {
    parts.push(`☔ ${weather.precipChance}%`);
  }
  if (weather.uvIndex != null) {
    parts.push(`UV ${Math.round(weather.uvIndex)}`);
  }
  return parts.join(' \u00B7 ');
}
